import { Box } from "@mui/material";
import React from "react";
import {
    findMatchingWord,
    findWordInGrid,
    normalizeFrenchCharacters,
    normalizeWord,
    removeHyphens,
} from "../puzzles/common/utils/PuzzlePlayLogic";
import StoryClueNumberCircle, { STORY_CLUE_CIRCLE_SELECTED_SX } from "./StoryClueNumberCircle";

/**
 * Resolve a story token to `coord.text` for selection/highlight state (aligned with clues list keys).
 */
export function resolveCanonicalWordKeyForStory(clickedToken, filledCoordinates) {
    if (!clickedToken || !filledCoordinates?.length) return null;
    const token = String(clickedToken).trim();
    if (!token) return null;
    for (const coord of filledCoordinates) {
        if (!coord?.text) continue;
        const cells = findWordInGrid(token, [coord]);
        if (cells?.length) return coord.text;
    }
    return null;
}

/** Same semantics as crossword admin (`filledCoordinates` JSON array). */
export function getWordsFromCoordinates(coordinates) {
    if (!coordinates) return [];
    return coordinates
        .filter((wordObj) => wordObj.text != null && wordObj.text !== undefined)
        .map((wordObj) => {
            const raw = wordObj.text != null ? String(wordObj.text) : "";
            const displayText = wordObj.reversed ? raw.split("").reverse().join("") : raw;
            return { ...wordObj, displayText };
        });
}

export function buildPuzzleWordMatchSet(coordinates) {
    const words = getWordsFromCoordinates(coordinates || []);
    const matchSet = new Set();
    words.forEach((w) => {
        if (w.text) matchSet.add(String(w.text).trim().toLowerCase());
        if (w.displayText) matchSet.add(String(w.displayText).trim().toLowerCase());
    });
    return matchSet;
}

/** First grid cell for clue numbering (aligned with {@link buildCellToClueNumberMap}). */
export function getFirstPositionForStoryClue(coord) {
    if (!coord?.positions || coord.positions.length === 0) return null;
    if (coord.direction === "scattered") return null;

    let firstPos;
    if (coord.reversed) {
        firstPos = coord.positions[coord.positions.length - 1];
    } else if (coord.direction === "vertical") {
        firstPos = coord.positions.reduce((min, pos) => (pos.row < min.row ? pos : min));
    } else if (coord.direction === "horizontal") {
        firstPos = coord.positions.reduce((min, pos) => (pos.col < min.col ? pos : min));
    } else {
        firstPos = coord.positions[0];
    }
    return firstPos;
}

/**
 * Map `"row,col"` → 1-based clue number (same sort order as CrossPlayMain `wordNumbers`).
 */
export function buildCellToClueNumberMap(filledCoordinates) {
    const cellToNumber = new Map();
    if (!filledCoordinates?.length) return cellToNumber;

    const startingPositions = new Map();
    filledCoordinates.forEach((coord) => {
        if (coord.direction === "scattered") return;
        const firstPos = getFirstPositionForStoryClue(coord);
        if (!firstPos) return;
        const cellKey = `${firstPos.row},${firstPos.col}`;
        if (!startingPositions.has(cellKey)) {
            startingPositions.set(cellKey, { row: firstPos.row, col: firstPos.col });
        }
    });

    const sortedPositions = Array.from(startingPositions.values()).sort((a, b) => {
        if (a.row !== b.row) return a.row - b.row;
        return a.col - b.col;
    });

    sortedPositions.forEach((pos, index) => {
        cellToNumber.set(`${pos.row},${pos.col}`, index + 1);
    });
    return cellToNumber;
}

/**
 * Lowercase story tokens → clue number for gray chips (uses `coord.text` / display forms).
 */
export function buildStoryTokenToClueNumberMap(filledCoordinates) {
    const tokenToNumber = new Map();
    if (!filledCoordinates?.length) return tokenToNumber;

    const cellToNumber = buildCellToClueNumberMap(filledCoordinates);
    const words = getWordsFromCoordinates(filledCoordinates);

    words.forEach((w) => {
        if (w.direction === "scattered") return;
        const firstPos = getFirstPositionForStoryClue(w);
        if (!firstPos) return;
        const n = cellToNumber.get(`${firstPos.row},${firstPos.col}`);
        if (n == null) return;

        if (w.text) tokenToNumber.set(String(w.text).trim().toLowerCase(), n);
        if (w.displayText) tokenToNumber.set(String(w.displayText).trim().toLowerCase(), n);
    });
    return tokenToNumber;
}

/**
 * Landing teaser (e.g. Story of the Day): puzzle vocabulary as blank grey blocks sized to word length — letters hidden.
 */
export function renderStoryLandingGreyBoxWords(storyText, matchSet) {
    const text = String(storyText);
    const parts = text.split(/(\b[\w']+\b)/g);
    return parts.map((part, i) => {
        const isWord = /^[\w']+$/.test(part);
        if (isWord && matchSet.has(part.toLowerCase())) {
            const chars = Math.max(part.length, 1);
            return (
                <Box
                    key={i}
                    component="span"
                    aria-hidden
                    sx={(theme) => ({
                        bgcolor: theme.palette.mode === "dark" ? "grey.800" : "grey.200",
                        display: "inline-block",
                        width: `${chars}ch`,
                        minHeight: "1.35em",
                        verticalAlign: "text-bottom",
                        borderRadius: 1,
                        boxSizing: "border-box",
                        boxDecorationBreak: "clone",
                        WebkitBoxDecorationBreak: "clone",
                        flexShrink: 0,
                    })}
                />
            );
        }
        return <span key={i}>{part}</span>;
    });
}

/** True when `canonicalCoordText` is already in `wordsFound` (normalized / hyphen-insensitive). */
export function isStoryWordResolvedForPlay(canonicalCoordText, wordsFound) {
    if (!canonicalCoordText || !Array.isArray(wordsFound) || wordsFound.length === 0) return false;
    const b = normalizeWord(String(canonicalCoordText));
    const bh = removeHyphens(b);
    return wordsFound.some((w) => {
        const a = normalizeWord(String(w));
        return a === b || removeHyphens(a) === bh;
    });
}

/** Story token equals the hidden-message phrase (single-token phrases). Uses hyphen/accent normalization. */
export function storyTokenMatchesHiddenMessagePhrase(token, hiddenPhrase) {
    if (!token || !hiddenPhrase) return false;
    const a = normalizeWord(String(token));
    const b = normalizeWord(String(hiddenPhrase));
    if (!a || !b) return false;
    const af = normalizeFrenchCharacters(a);
    const bf = normalizeFrenchCharacters(b);
    return (
        a === b ||
        af === bf ||
        removeHyphens(a) === removeHyphens(b) ||
        removeHyphens(af) === removeHyphens(bf)
    );
}

/** @typedef {'numberedCircle' | 'greyBox'} StoryWordMarkerVariant */

/**
 * Whole-word tokens — crossword: numbered circles; grid story: words shown inline with marker-style highlight fill.
 *
 * @param {Map<string, number>} tokenToClueNumber
 * @param {Array} [filledCoordinates]
 * @param {(coordText: string) => void} [onSelectCanonicalWord] — e.g. same handler as clues row tap
 * @param {StoryWordMarkerVariant} [markerVariant] — `greyBox`: prose words as highlighted text (still no clue numbers)
 * @param {Set<string>|null} [emphasizedPuzzleWords] — puzzle word strings chosen in Words to Find (`greyBox` only): draw a board ring in the story
 * @param {string[]|null} [puzzleWords] — canonical word list; required with `emphasizedPuzzleWords` for matching accents/hyphens
 * @param {number|null} [selectedClueNumber] — crossword story: highlight this clue’s circle in orange when set
 * @param {string[]|null} [resolvedWordsFound] — crossword numbered circles: solved words show as text. Grid `greyBox`: when an array is passed (including `[]`), story vocabulary stays blank until each word is matched here; omit or pass `null` to always show words (legacy).
 * @param {{ revealed: boolean, phrase: string }|null} [hiddenMessageStoryGate] — hidden-message puzzles only: when set, a story token equal to `phrase` stays blank until `revealed` is true (full puzzle solve), even if that word was found early on the grid.
 */
export function renderStoryWordFindMarkers(
    storyText,
    matchSet,
    tokenToClueNumber,
    filledCoordinates,
    onSelectCanonicalWord,
    markerVariant = "numberedCircle",
    emphasizedPuzzleWords = null,
    puzzleWords = null,
    selectedClueNumber = null,
    resolvedWordsFound = null,
    hiddenMessageStoryGate = null
) {
    const coords = filledCoordinates?.length ? filledCoordinates : [];
    const clickable =
        typeof onSelectCanonicalWord === "function" && coords.length > 0;
    const text = String(storyText);
    const parts = text.split(/(\b[\w']+\b)/g);
    return parts.map((part, i) => {
        const isWord = /^[\w']+$/.test(part);
        const keyLower = part.toLowerCase();
        if (isWord && matchSet.has(keyLower)) {
            if (markerVariant === "greyBox") {
                const listWordResolved =
                    Array.isArray(puzzleWords) && puzzleWords.length
                        ? findMatchingWord(part, puzzleWords)
                        : null;
                const selectedFromWordsDrawer =
                    listWordResolved &&
                    emphasizedPuzzleWords instanceof Set &&
                    emphasizedPuzzleWords.size > 0 &&
                    emphasizedPuzzleWords.has(listWordResolved);

                const trackResolved =
                    resolvedWordsFound != null && Array.isArray(resolvedWordsFound);
                const canonical = resolveCanonicalWordKeyForStory(part, coords);
                const hmGate = hiddenMessageStoryGate;
                const tokenIsHmPhrase =
                    hmGate &&
                    typeof hmGate.phrase === "string" &&
                    String(hmGate.phrase).trim() !== "" &&
                    storyTokenMatchesHiddenMessagePhrase(part, hmGate.phrase);

                let resolvedForPlay;
                if (!trackResolved) {
                    resolvedForPlay = true;
                } else if (tokenIsHmPhrase) {
                    resolvedForPlay = Boolean(hmGate.revealed);
                } else if (!canonical) {
                    resolvedForPlay = true;
                } else {
                    resolvedForPlay = isStoryWordResolvedForPlay(canonical, resolvedWordsFound);
                }

                const greyBlankSx = (theme) => ({
                    bgcolor: theme.palette.mode === "dark" ? "grey.800" : "grey.200",
                    display: "inline-block",
                    width: `${Math.max(part.length, 1)}ch`,
                    minHeight: "1.35em",
                    verticalAlign: "text-bottom",
                    borderRadius: 1,
                    boxSizing: "border-box",
                    boxDecorationBreak: "clone",
                    WebkitBoxDecorationBreak: "clone",
                    flexShrink: 0,
                    cursor: "default",
                    pointerEvents: "none",
                    userSelect: "none",
                    ...(selectedFromWordsDrawer && {
                        boxShadow: `0 0 0 2px ${theme.palette.primary.main}`,
                    }),
                });

                const selectFromToken = () => {
                    const w = resolveCanonicalWordKeyForStory(part, coords);
                    if (w && typeof onSelectCanonicalWord === "function") {
                        onSelectCanonicalWord(w);
                    }
                };

                if (trackResolved && !resolvedForPlay) {
                    /* No tap action until the word is found — avoids highlighting Words to Find from story blanks */
                    return (
                        <Box key={i} component="span" sx={(theme) => greyBlankSx(theme)} aria-hidden />
                    );
                }

                const highlightedStoryWordSx = (theme) => ({
                    bgcolor:
                        theme.palette.mode === "dark"
                            ? selectedFromWordsDrawer
                                ? "rgba(255, 235, 59, 0.32)"
                                : "rgba(255, 235, 59, 0.18)"
                            : selectedFromWordsDrawer
                              ? "#FFE082"
                              : "#FFF9C4",
                    color: "text.primary",
                    display: "inline",
                    px: 0.25,
                    py: 0,
                    borderRadius: 1,
                    fontWeight: selectedFromWordsDrawer ? 700 : 600,
                    boxDecorationBreak: "clone",
                    WebkitBoxDecorationBreak: "clone",
                    verticalAlign: "baseline",
                    ...(selectedFromWordsDrawer && {
                        boxShadow: `0 0 0 2px ${theme.palette.primary.main}`,
                    }),
                    position: selectedFromWordsDrawer ? "relative" : undefined,
                    zIndex: selectedFromWordsDrawer ? 1 : undefined,
                });
                if (clickable) {
                    return (
                        <Box
                            key={i}
                            component="button"
                            type="button"
                            aria-label="Highlight puzzle word"
                            onClick={(e) => {
                                e.preventDefault();
                                selectFromToken();
                            }}
                            sx={(theme) => ({
                                ...highlightedStoryWordSx(theme),
                                mx: 0.125,
                                border: "none",
                                cursor: "pointer",
                                margin: 0,
                                paddingBlock: 0,
                                paddingInline: theme.spacing(0.25),
                                font: "inherit",
                                appearance: "none",
                                lineHeight: 1.65,
                                "&:focus-visible": {
                                    outline: "2px solid",
                                    outlineOffset: 2,
                                    outlineColor: "primary.main",
                                },
                                "&:hover": {
                                    bgcolor:
                                        theme.palette.mode === "dark"
                                            ? "rgba(255, 235, 59, 0.28)"
                                            : "#FFF59D",
                                },
                            })}
                        >
                            {part}
                        </Box>
                    );
                }
                return (
                    <Box
                        key={i}
                        component="span"
                        sx={(theme) => ({ ...highlightedStoryWordSx(theme), mx: 0.125 })}
                    >
                        {part}
                    </Box>
                );
            }

            const canonical = resolveCanonicalWordKeyForStory(part, coords);
            const resolved =
                canonical &&
                Array.isArray(resolvedWordsFound) &&
                resolvedWordsFound.length > 0 &&
                isStoryWordResolvedForPlay(canonical, resolvedWordsFound);

            const clueNum =
                tokenToClueNumber instanceof Map ? tokenToClueNumber.get(keyLower) : undefined;
            const circleSelected =
                selectedClueNumber != null &&
                clueNum != null &&
                Number(selectedClueNumber) === Number(clueNum);
            const circleSx = circleSelected ? STORY_CLUE_CIRCLE_SELECTED_SX : undefined;
            const sharedWrapperSx = {
                display: "inline-flex",
                alignItems: "center",
                verticalAlign: "middle",
                lineHeight: 1.65,
                mx: 0.125,
                position: "relative",
                top: "0.125em",
            };
            const selectFromToken = () => {
                const w = resolveCanonicalWordKeyForStory(part, coords);
                if (w && typeof onSelectCanonicalWord === "function") {
                    onSelectCanonicalWord(w);
                }
            };

            if (resolved) {
                const resolvedWordSx = (theme) => ({
                    bgcolor: circleSelected
                        ? theme.palette.mode === "dark"
                            ? "rgba(255, 152, 0, 0.28)"
                            : "#FFE0B2"
                        : theme.palette.mode === "dark"
                          ? "rgba(76, 175, 80, 0.38)"
                          : "#C4FFE0",
                    color: "text.primary",
                    fontWeight: circleSelected ? 700 : 600,
                    px: 0.35,
                    py: 0.125,
                    borderRadius: 1,
                    boxDecorationBreak: "clone",
                    WebkitBoxDecorationBreak: "clone",
                    verticalAlign: "baseline",
                    display: "inline",
                    lineHeight: 1.65,
                    ...(circleSelected && {
                        boxShadow: `0 0 0 2px ${theme.palette.warning.main}`,
                    }),
                });
                if (clickable) {
                    return (
                        <Box
                            key={i}
                            component="button"
                            type="button"
                            aria-label={
                                clueNum != null
                                    ? `Select clue ${clueNum}, resolved`
                                    : `Resolved word ${part}`
                            }
                            onClick={(e) => {
                                e.preventDefault();
                                selectFromToken();
                            }}
                            sx={(theme) => ({
                                ...resolvedWordSx(theme),
                                mx: 0.125,
                                border: "none",
                                cursor: "pointer",
                                margin: 0,
                                font: "inherit",
                                appearance: "none",
                                "&:focus-visible": {
                                    outline: "2px solid",
                                    outlineOffset: 2,
                                    outlineColor: "primary.main",
                                },
                                "&:hover": {
                                    opacity: 0.92,
                                },
                            })}
                        >
                            {part}
                        </Box>
                    );
                }
                return (
                    <Box
                        key={i}
                        component="span"
                        sx={(theme) => ({ ...resolvedWordSx(theme), mx: 0.125 })}
                    >
                        {part}
                    </Box>
                );
            }

            if (clickable && clueNum != null) {
                return (
                    <Box
                        key={i}
                        component="button"
                        type="button"
                        aria-label={`Select clue ${clueNum}`}
                        onClick={(e) => {
                            e.preventDefault();
                            selectFromToken();
                        }}
                        sx={{
                            ...sharedWrapperSx,
                            border: "none",
                            cursor: "pointer",
                            padding: 0,
                            margin: 0,
                            background: "none",
                            font: "inherit",
                            lineHeight: 1.65,
                            appearance: "none",
                            verticalAlign: "middle",
                            borderRadius: "50%",
                            transition: "transform 0.15s ease, box-shadow 0.15s ease",
                            ...(circleSelected && {
                                boxShadow: "0 0 0 3px rgba(255, 152, 0, 0.55)",
                            }),
                            "&:focus-visible": {
                                outline: "2px solid",
                                outlineOffset: 2,
                                outlineColor: "primary.main",
                                borderRadius: "50%",
                            },
                            "&:hover": { opacity: circleSelected ? 1 : 0.92 },
                        }}
                    >
                        <StoryClueNumberCircle sx={circleSx}>{clueNum}</StoryClueNumberCircle>
                    </Box>
                );
            }

            const inner =
                clueNum != null ? (
                    <StoryClueNumberCircle sx={circleSx}>{clueNum}</StoryClueNumberCircle>
                ) : null;
            return (
                <Box key={i} component="span" sx={sharedWrapperSx}>
                    {inner}
                </Box>
            );
        }
        return <span key={i}>{part}</span>;
    });
}
